import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, User, Mail, Lock, Phone } from 'lucide-react';

const translations = {
  it: {
    backToHome: 'Torna alla Home',
    title: 'Registrati',
    subtitle: 'Crea il tuo account per prenotare il tuo soggiorno',
    firstName: 'Nome',
    lastName: 'Cognome',
    email: 'Email',
    phone: 'Telefono (opzionale)',
    password: 'Password',
    passwordHint: 'Minimo 6 caratteri',
    registerButton: 'Registrati',
    registering: 'Registrazione in corso...',
    alreadyHaveAccount: 'Hai già un account?',
    login: 'Accedi',
    welcome: 'Benvenuto',
    bookingCode: 'Il tuo codice prenotazione è',
    registerError: 'Errore durante la registrazione',
    heroTitle: 'Unisciti alla',
    heroTitle2: 'Nostra Famiglia',
    heroSubtitle: 'Registrati per accedere a vantaggi esclusivi e al programma fedeltà.'
  },
  en: {
    backToHome: 'Back to Home',
    title: 'Sign Up',
    subtitle: 'Create your account to book your stay',
    firstName: 'First Name',
    lastName: 'Last Name',
    email: 'Email',
    phone: 'Phone (optional)',
    password: 'Password',
    passwordHint: 'Minimum 6 characters',
    registerButton: 'Sign Up',
    registering: 'Signing up...',
    alreadyHaveAccount: 'Already have an account?',
    login: 'Log in',
    welcome: 'Welcome',
    bookingCode: 'Your booking code is',
    registerError: 'Registration error',
    heroTitle: 'Join',
    heroTitle2: 'Our Family',
    heroSubtitle: 'Sign up to access exclusive benefits and the loyalty program.'
  }
};

export default function RegisterPage() {
  const { language } = useLanguage();
  const t = translations[language] || translations.it;
  const [formData, setFormData] = useState({
    nome: '',
    cognome: '',
    email: '',
    password: '',
    telefono: ''
  });
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await register(formData);
      toast.success(`${t.welcome}, ${user.nome}! ${t.bookingCode}: ${user.codice_prenotazione}`);
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || t.registerError);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F9F7] flex">
      {/* Left side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Link 
            to="/" 
            className="inline-flex items-center text-[#4A5568] hover:text-[#C5A059] mb-8 font-manrope text-sm transition-colors"
            data-testid="back-home-link"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t.backToHome}
          </Link>

          <div className="mb-8">
            <h1 className="font-cinzel text-3xl text-[#1A202C] mb-2">{t.title}</h1>
            <p className="font-manrope text-[#4A5568]">
              {t.subtitle}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="nome" className="font-manrope text-sm font-medium text-[#4A5568]">
                  {t.firstName}
                </Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#C5A059]" />
                  <Input
                    id="nome"
                    name="nome"
                    type="text"
                    value={formData.nome}
                    onChange={handleChange}
                    placeholder="Mario"
                    className="pl-10 border-[#E2E8F0] focus:border-[#C5A059] focus:ring-[#C5A059]"
                    required
                    data-testid="input-nome"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="cognome" className="font-manrope text-sm font-medium text-[#4A5568]">
                  {t.lastName}
                </Label>
                <Input
                  id="cognome"
                  name="cognome"
                  type="text"
                  value={formData.cognome}
                  onChange={handleChange}
                  placeholder="Rossi"
                  className="border-[#E2E8F0] focus:border-[#C5A059] focus:ring-[#C5A059]"
                  required
                  data-testid="input-cognome"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="font-manrope text-sm font-medium text-[#4A5568]">
                {t.email}
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#C5A059]" />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="mario.rossi@email.it"
                  className="pl-10 border-[#E2E8F0] focus:border-[#C5A059] focus:ring-[#C5A059]"
                  required
                  data-testid="input-email"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="telefono" className="font-manrope text-sm font-medium text-[#4A5568]">
                {t.phone}
              </Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#C5A059]" />
                <Input
                  id="telefono"
                  name="telefono"
                  type="tel"
                  value={formData.telefono}
                  onChange={handleChange}
                  placeholder="+39 333 1234567"
                  className="pl-10 border-[#E2E8F0] focus:border-[#C5A059] focus:ring-[#C5A059]"
                  data-testid="input-telefono"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="font-manrope text-sm font-medium text-[#4A5568]">
                {t.password}
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#C5A059]" />
                <Input
                  id="password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="••••••••"
                  className="pl-10 border-[#E2E8F0] focus:border-[#C5A059] focus:ring-[#C5A059]"
                  required
                  minLength={6}
                  data-testid="input-password"
                />
              </div>
              <p className="text-xs text-[#4A5568] font-manrope">
                {t.passwordHint}
              </p>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#C5A059] hover:bg-[#B08D45] text-white font-cinzel tracking-widest uppercase py-6 mt-6"
              data-testid="submit-register-btn"
            >
              {loading ? t.registering : t.registerButton}
            </Button>
          </form>

          <p className="mt-8 text-center font-manrope text-sm text-[#4A5568]">
            {t.alreadyHaveAccount}{' '}
            <Link 
              to="/login" 
              className="text-[#C5A059] hover:underline font-medium"
              data-testid="login-link"
            >
              {t.login}
            </Link>
          </p>
        </div>
      </div>

      {/* Right side - Image */}
      <div 
        className="hidden lg:block lg:w-1/2 bg-cover bg-center relative"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1633605015660-b0f2dbad3bf2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBob3RlbCUyMGJlZHJvb20lMjBicmlnaHQlMjBtb2Rlcm4lMjBpbnRlcmlvciUyMGdvbGQlMjBhY2NlbnRzfGVufDB8fHx8MTc2NjM1NTUwM3ww&ixlib=rb-4.1.0&q=85')`
        }}
      >
        <div className="absolute inset-0 bg-[#1A202C]/50" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <h2 className="font-cinzel text-4xl mb-4">
            {t.heroTitle}<br />{t.heroTitle2}
          </h2>
          <p className="font-manrope text-gray-300">
            {t.heroSubtitle}
          </p>
        </div>
      </div>
    </div>
  );
}
